import React, { useContext, useEffect, useState, useMemo } from 'react'
import { Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb'
import { DOCUMENT_TYPE } from '@trustero/trustero-api-web/lib/attachment/attachment_pb'
import {
  SetLinkRequest,
  LinkDoc,
} from '@trustero/trustero-api-web/lib/linker/linker_pb'
import { useAuthorizedGrpcClient } from 'src/adapter/grpcClient'
import { LinkerPromiseClient } from '@trustero/trustero-api-web/lib/linker/linker_grpc_web_pb'
import { ReceptorRecord } from '@trustero/trustero-api-web/lib/agent/receptor_pb'
import {
  useFolderScanReceptor,
  useHasReceptor,
} from 'src/pages/Receptors/receptors.hooks'
import { useDocumentPickerIfAvailable } from 'src/components/DocumentPicker'
import { PickerConfiguration } from '../modals/Google Drive Wizard/GoogleDriveWizard.constants'
import { Source } from '../Linkers.constants'
import { ExtendedDocumentObject, LINKER_MODEL_IDS } from '../Linkers.constants'

interface OnSubmitProps {
  caption: string
  isViaRequest?: boolean
  relevantDate?: Timestamp
}

type GoogleDriveContextType = {
  pickerConfig: PickerConfiguration | null
  setPickerConfig: React.Dispatch<
    React.SetStateAction<PickerConfiguration | null>
  >
  filesSelected?: ExtendedDocumentObject[]
  setFilesSelected?: React.Dispatch<
    React.SetStateAction<ExtendedDocumentObject[]>
  >
  onSubmit: (props: OnSubmitProps) => Promise<string[]>
  source: Source
  setSource: (source: Source) => void
  subjectIds: Array<string>
  setSubjectIds: (subjectId: Array<string>) => void
  hasGDriveReceptor: boolean
  /** The account's Google Drive receptor, used when a picker is opened without one. */
  receptor?: ReceptorRecord
}

const GoogleDriveContext = React.createContext<
  GoogleDriveContextType | undefined
>(undefined)

export const useGoogleDriveContext = (): GoogleDriveContextType => {
  const context = useContext(GoogleDriveContext)
  if (!context) {
    throw new Error(
      'useGoogleDriveContext must be used within a GoogleDriveContextProvider',
    )
  }
  return context
}

export const GoogleDriveContextProvider = ({
  children,
}: {
  children: JSX.Element
}): JSX.Element => {
  const { data: HasReceptor } = useHasReceptor(
    LINKER_MODEL_IDS.GOOGLE_DRIVE,
    true,
  )
  const hasGDriveReceptor = useMemo(
    () => HasReceptor?.getHasReceptor() || false,
    [HasReceptor],
  )
  // The fallback connection when a picker is opened without one: the enabled
  // row with valid credentials, not whichever row the database lists first.
  const receptor = useFolderScanReceptor(LINKER_MODEL_IDS.GOOGLE_DRIVE)
  // The connection the picked files came from. An account can hold several
  // Google Drive connections; the link must go through the one browsed.
  const [linkReceptor, setLinkReceptor] = useState<ReceptorRecord | undefined>(
    undefined,
  )
  const [pickerConfig, setPickerConfig] = useState<PickerConfiguration | null>(
    null,
  )
  const linkerClient = useAuthorizedGrpcClient(LinkerPromiseClient)
  const [filesSelected, setFilesSelected] = useState<ExtendedDocumentObject[]>(
    [],
  )
  const [source, setSource] = useState<Source>('Evidence')
  const [subjectIds, setSubjectIds] = useState<Array<string>>([])

  // The document picker browses Drive under the receptor's identity and
  // hands the picked nodes over here, in the shape the linking flows expect.
  const documentPicker = useDocumentPickerIfAvailable()
  const selection = documentPicker?.selection
  const clearSelection = documentPicker?.clearSelection
  useEffect(() => {
    if (!selection || !clearSelection || selection.source === 'FolderScan')
      return
    if (selection.receptor.getModelid() !== LINKER_MODEL_IDS.GOOGLE_DRIVE)
      return
    setLinkReceptor(selection.receptor)
    setFilesSelected(
      selection.nodes.map(
        (node): ExtendedDocumentObject =>
          ({
            id: node.id,
            name: node.title,
            url: node.url || '',
            mimeType: node.mimeType || '',
            sizeBytes: node.sizeBytes,
            lastEditedUtc: node.modifiedAt
              ? new Date(node.modifiedAt).getTime()
              : undefined,
          } as ExtendedDocumentObject),
      ),
    )
    clearSelection()
  }, [selection, clearSelection])
  const onSubmit = async ({
    caption,
    relevantDate,
    isViaRequest = false,
  }: OnSubmitProps): Promise<string[]> => {
    const request = new SetLinkRequest().setReceptorId(
      (linkReceptor ?? receptor)?.getId() || '',
    )
    const documents = filesSelected.map((doc) => {
      const linkDoc = new LinkDoc()
        .setImportKey(`${doc.id}:${source}`)
        .setImportLink(doc.url)
        .setSubjectIdsList(subjectIds)
        .setDocumentType(
          source === 'Evidence'
            ? DOCUMENT_TYPE.EVIDENCE
            : DOCUMENT_TYPE.POLICYDOC,
        )
        .setRelevantDate(relevantDate || undefined)
        .setIsViaRequest(isViaRequest)
        .setCaption(caption && caption.length > 0 ? caption : doc.name)
        .setFileName(doc.name)
      return linkDoc
    })
    setFilesSelected([])
    setLinkReceptor(undefined)
    request.setDocumentsList(documents)
    const response = await linkerClient.setLink(request)
    return response.getEvidenceIdsList()
  }

  return (
    <GoogleDriveContext.Provider
      value={{
        hasGDriveReceptor,
        pickerConfig,
        setPickerConfig,
        filesSelected,
        setFilesSelected,
        onSubmit,
        source,
        setSource,
        subjectIds,
        setSubjectIds,
        receptor,
      }}
    >
      {children}
    </GoogleDriveContext.Provider>
  )
}
